import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Loader2, ChevronLeft, ChevronRight, Settings, X, Navigation } from 'lucide-react';
import canvassingMobileService from '../../services/canvassingMobileService';
import NewLeadView from './NewLeadView';
import LeadCard from './LeadCard';
import CallLogModal from './CallLogModal';
import CallbackModal from './CallbackModal';
import ConversionFlow from './ConversionFlow';

const TABS = [
  { id: 'new', label: 'New Lead' },
  { id: 'recent', label: 'Recent Leads' },
  { id: 'callbacks', label: 'Callbacks' },
];

/**
 * Saved position cache — persists to localStorage so it survives page refreshes.
 * Position is valid for 10 minutes before we fetch a fresh one.
 */
const POSITION_KEY = 'canvassing_last_position';
const POSITION_MAX_AGE = 10 * 60 * 1000; // 10 minutes

const getSavedPosition = () => {
  try {
    const saved = JSON.parse(localStorage.getItem(POSITION_KEY));
    if (saved && Date.now() - saved.ts < POSITION_MAX_AGE) {
      return { lat: saved.lat, lng: saved.lng };
    }
  } catch {}
  return null;
};

const savePosition = (pos) => {
  if (pos) {
    localStorage.setItem(POSITION_KEY, JSON.stringify({ lat: pos.lat, lng: pos.lng, ts: Date.now() }));
  }
};

/**
 * Get device position via Geolocation API.
 * Returns { lat, lng } or null on error/denial. 5-second timeout.
 */
const getPosition = () => {
  return new Promise((resolve) => {
    if (!navigator.geolocation) {
      resolve(null);
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const result = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        savePosition(result);
        resolve(result);
      },
      () => resolve(null),
      { enableHighAccuracy: false, timeout: 5000, maximumAge: 60000 }
    );
  });
};

const formatDate = (dateStr) => {
  const d = new Date(dateStr);
  const day = d.getDate();
  const suffix = [11,12,13].includes(day) ? 'th' : {1:'st',2:'nd',3:'rd'}[day%10] || 'th';
  const weekday = d.toLocaleDateString('en-GB', { weekday: 'long' });
  const month = d.toLocaleDateString('en-GB', { month: 'short' });
  const year = d.getFullYear();
  const time = d.toLocaleTimeString('en-GB', { hour: 'numeric', minute: '2-digit', hour12: true }).toLowerCase();
  return `${weekday} ${day}${suffix} ${month} ${year}, ${time}`;
};

const CanvassingMobile = ({ userProfile }) => {
  const [activeTab, setActiveTab] = useState('new');

  // Data state
  const [newLead, setNewLead] = useState(null);
  const [newLeadNotes, setNewLeadNotes] = useState([]);
  const [recentLeads, setRecentLeads] = useState({ leads: [], pagination: {} });
  const [callbacks, setCallbacks] = useState([]);
  const [selectedLead, setSelectedLead] = useState(null);
  const [selectedLeadNotes, setSelectedLeadNotes] = useState([]);

  // UI state
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [recentPage, setRecentPage] = useState(1);

  // Settings state
  const [showSettings, setShowSettings] = useState(false);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(
    () => localStorage.getItem('canvassing_category') || ''
  );
  const [sortByNearest, setSortByNearest] = useState(
    () => localStorage.getItem('canvassing_sort_nearest') !== 'false'
  );
  const lastPosition = useRef(getSavedPosition());

  // Modals
  const [callLogLead, setCallLogLead] = useState(null);
  const [callbackLead, setCallbackLead] = useState(null);
  const [conversionLead, setConversionLead] = useState(null);

  // === DATA LOADING ===

  // Fire-and-forget GPS refresh — updates cache for next load
  const refreshPosition = useCallback(() => {
    getPosition().then(pos => { if (pos) lastPosition.current = pos; });
  }, []);

  const loadNextNewLead = useCallback(async (categoryId, lat = null, lng = null) => {
    try {
      if (lat == null && sortByNearest && lastPosition.current) {
        lat = lastPosition.current.lat;
        lng = lastPosition.current.lng;
      }
      const data = await canvassingMobileService.getNextNewLead(categoryId || null, lat, lng);
      setNewLead(data.lead);
      setNewLeadNotes(data.notes || []);
      // Always refresh position in background after loading a lead
      refreshPosition();
    } catch (err) {
      console.error('Failed to load next new lead:', err);
    }
  }, [sortByNearest, refreshPosition]);

  const loadRecentLeads = useCallback(async (page = 1) => {
    try {
      const data = await canvassingMobileService.getRecentLeads(page, 20);
      setRecentLeads(data);
      setRecentPage(page);
    } catch (err) {
      console.error('Failed to load recent leads:', err);
    }
  }, []);

  const loadCallbacks = useCallback(async () => {
    try {
      const data = await canvassingMobileService.getDueCallbacks('all');
      setCallbacks(data.callbacks || []);
    } catch (err) {
      console.error('Failed to load callbacks:', err);
    }
  }, []);

  const loadCategories = useCallback(async () => {
    try {
      const data = await canvassingMobileService.getCategories();
      setCategories(data.categories || []);
    } catch (err) {
      console.error('Failed to load categories:', err);
    }
  }, []);

  // Initial load — uses cached position from localStorage (instant)
  useEffect(() => {
    const init = async () => {
      setLoading(true);
      try {
        await Promise.all([
          loadNextNewLead(selectedCategory),
          loadCategories()
        ]);
      } catch (err) {
        setError('Failed to load canvassing data');
      } finally {
        setLoading(false);
      }
      // Refresh GPS in background for next time
      if (sortByNearest) {
        getPosition().then(pos => { if (pos) lastPosition.current = pos; });
      }
    };
    init();
  }, [loadNextNewLead, loadCategories, selectedCategory, sortByNearest]);

  // Load tab-specific data when switching
  useEffect(() => {
    if (activeTab === 'recent') loadRecentLeads(1);
    if (activeTab === 'callbacks') loadCallbacks();
  }, [activeTab, loadRecentLeads, loadCallbacks]);

  // === SETTINGS ===

  const handleCategoryChange = (categoryId) => {
    setSelectedCategory(categoryId);
    if (categoryId) {
      localStorage.setItem('canvassing_category', categoryId);
    } else {
      localStorage.removeItem('canvassing_category');
    }
    setShowSettings(false);
    loadNextNewLead(categoryId);
  };

  const handleSortToggle = (checked) => {
    setSortByNearest(checked);
    localStorage.setItem('canvassing_sort_nearest', checked ? 'true' : 'false');
    if (checked && lastPosition.current) {
      loadNextNewLead(selectedCategory, lastPosition.current.lat, lastPosition.current.lng);
    } else {
      loadNextNewLead(selectedCategory);
    }
  };

  // === ACTIONS ===

  const handleCall = (lead) => {
    setTimeout(() => setCallLogLead(lead), 500);
  };

  const handleStatusChange = async (leadId, status, callOutcome) => {
    setSaving(true);
    try {
      await canvassingMobileService.updateStatus(leadId, {
        status,
        call_outcome: callOutcome,
        notes: null
      });
      if (activeTab === 'new') await loadNextNewLead(selectedCategory);
      if (activeTab === 'recent') await loadRecentLeads(recentPage);
      if (activeTab === 'callbacks') await loadCallbacks();
    } catch (err) {
      setError('Failed to update status');
    } finally {
      setSaving(false);
    }
  };

  const handleCallLogSave = async (leadId, callData) => {
    setSaving(true);
    try {
      await canvassingMobileService.addCallLog(leadId, callData);
      setCallLogLead(null);
      if (activeTab === 'new') await loadNextNewLead(selectedCategory);
      if (activeTab === 'recent') await loadRecentLeads(recentPage);
    } catch (err) {
      setError('Failed to save call log');
    } finally {
      setSaving(false);
    }
  };

  const handleCallbackSave = async (callbackData) => {
    setSaving(true);
    try {
      await canvassingMobileService.scheduleCallback(callbackData);
      setCallbackLead(null);
      if (activeTab === 'new') await loadNextNewLead(selectedCategory);
      if (activeTab === 'recent') await loadRecentLeads(recentPage);
      if (activeTab === 'callbacks') await loadCallbacks();
    } catch (err) {
      setError('Failed to schedule callback');
    } finally {
      setSaving(false);
    }
  };

  const handleConvert = async (leadId, conversionData) => {
    setSaving(true);
    try {
      const result = await canvassingMobileService.convertLead(leadId, conversionData);
      if (activeTab === 'new') await loadNextNewLead(selectedCategory);
      if (activeTab === 'recent') await loadRecentLeads(recentPage);
      if (activeTab === 'callbacks') await loadCallbacks();
      return result;
    } catch (err) {
      setError('Failed to convert lead');
      throw err;
    } finally {
      setSaving(false);
    }
  };

  const handleConversionClose = () => {
    setConversionLead(null);
  };

  const handleSelectLead = async (lead) => {
    setSelectedLead(lead);
    try {
      const data = await canvassingMobileService.getLeadNotes(lead.id);
      setSelectedLeadNotes(data.notes || []);
    } catch {
      setSelectedLeadNotes([]);
    }
  };

  const handleBackFromSelected = () => {
    setSelectedLead(null);
    setSelectedLeadNotes([]);
    if (activeTab === 'recent') loadRecentLeads(recentPage);
    if (activeTab === 'callbacks') loadCallbacks();
  };

  // === RENDER ===

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="animate-spin text-blue-600" size={32} />
      </div>
    );
  }

  // If a lead is selected from recent/callbacks, show full view
  if (selectedLead && (activeTab === 'recent' || activeTab === 'callbacks')) {
    return (
      <div className="flex flex-col h-full bg-gray-100">
        <div className="bg-white border-b px-3 py-2 flex items-center gap-2">
          <button
            onClick={handleBackFromSelected}
            className="flex items-center gap-1 text-sm text-blue-600 font-medium min-h-[40px] px-2"
          >
            <ChevronLeft size={18} />
            Back
          </button>
          <span className="text-xs text-gray-400">
            {activeTab === 'recent' ? 'Recent Leads' : 'Callbacks'}
          </span>
        </div>
        <NewLeadView
          lead={selectedLead}
          notes={selectedLeadNotes}
          userProfile={userProfile}
          onCall={handleCall}
          onStatusChange={handleStatusChange}
          onCallback={setCallbackLead}
          onConvert={setConversionLead}
          disabled={saving}
        />

        {callLogLead && (
          <CallLogModal lead={callLogLead} onSave={handleCallLogSave} onClose={() => setCallLogLead(null)} saving={saving} />
        )}
        {callbackLead && (
          <CallbackModal lead={callbackLead} onSave={handleCallbackSave} onClose={() => setCallbackLead(null)} saving={saving} />
        )}
        {conversionLead && (
          <ConversionFlow lead={conversionLead} onConvert={handleConvert} onClose={handleConversionClose} saving={saving} />
        )}
      </div>
    );
  }

  const pagination = activeTab === 'recent' ? recentLeads.pagination : null;
  const selectedCatName = categories.find(c => String(c.id) === String(selectedCategory))?.category_name;

  return (
    <div className="flex flex-col h-full bg-gray-100">
      {/* Tabs + Settings cog */}
      <div className="flex items-center bg-white border-b px-2 pt-1">
        <div className="flex flex-1">
          {TABS.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex-1 py-2.5 text-sm font-semibold rounded-t-lg transition-all
                ${activeTab === tab.id
                  ? 'text-blue-700 border-b-2 border-blue-700 bg-blue-50'
                  : 'text-gray-500 hover:text-gray-700'
                }`}
            >
              {tab.label}
              {tab.id === 'callbacks' && callbacks.length > 0 && (
                <span className="ml-1 bg-amber-500 text-white text-xs px-1.5 py-0.5 rounded-full">
                  {callbacks.length}
                </span>
              )}
            </button>
          ))}
        </div>
        <button
          onClick={() => setShowSettings(!showSettings)}
          className={`ml-1 p-2 rounded-lg transition-colors ${showSettings || selectedCategory ? 'text-blue-600 bg-blue-50' : 'text-gray-400 hover:text-gray-600'}`}
        >
          <Settings size={18} />
        </button>
      </div>

      {/* Filter indicators */}
      {!showSettings && (selectedCategory || sortByNearest) && (
        <div className="flex items-center gap-3 px-3 py-1.5 bg-blue-50 border-b border-blue-100">
          {selectedCategory && (
            <div className="flex items-center gap-1">
              <span className="text-xs text-blue-700">Filtering: <strong>{selectedCatName || 'Category'}</strong></span>
              <button
                onClick={() => handleCategoryChange('')}
                className="text-blue-500 hover:text-blue-700"
              >
                <X size={14} />
              </button>
            </div>
          )}
          {sortByNearest && (
            <div className="flex items-center gap-1">
              <Navigation size={12} className="text-blue-600" />
              <span className="text-xs text-blue-700">Nearest first</span>
            </div>
          )}
        </div>
      )}

      {/* Settings panel */}
      {showSettings && (
        <div className="bg-white border-b shadow-sm p-3 space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-gray-700">Settings</h3>
            <button onClick={() => setShowSettings(false)} className="text-gray-400 hover:text-gray-600">
              <X size={18} />
            </button>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Lead Category</label>
            <select
              value={selectedCategory}
              onChange={e => handleCategoryChange(e.target.value)}
              className="w-full rounded-xl border-gray-300 text-sm p-2.5 min-h-[44px]"
            >
              <option value="">All Categories</option>
              {categories.map(cat => (
                <option key={cat.id} value={cat.id}>
                  {cat.category_name} ({cat.lead_count})
                </option>
              ))}
            </select>
          </div>
          <label className="flex items-center gap-3 min-h-[44px] cursor-pointer">
            <input
              type="checkbox"
              checked={sortByNearest}
              onChange={e => handleSortToggle(e.target.checked)}
              className="w-5 h-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <div className="flex items-center gap-1.5">
              <Navigation size={14} className="text-blue-600" />
              <span className="text-sm text-gray-700 font-medium">Sort by nearest to me</span>
            </div>
          </label>
        </div>
      )}

      {/* Error banner */}
      {error && (
        <div className="mx-3 mt-2 p-3 bg-red-50 text-red-700 text-sm rounded-xl flex items-center justify-between">
          <span>{error}</span>
          <button onClick={() => setError(null)} className="text-red-500 font-bold ml-2">&times;</button>
        </div>
      )}

      {/* Tab content */}
      {activeTab === 'new' && (
        <NewLeadView
          lead={newLead}
          notes={newLeadNotes}
          userProfile={userProfile}
          onCall={handleCall}
          onStatusChange={handleStatusChange}
          onCallback={setCallbackLead}
          onConvert={setConversionLead}
          disabled={saving}
        />
      )}

      {activeTab === 'recent' && (
        <div className="flex-1 overflow-y-auto p-3 space-y-2">
          {recentLeads.leads.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <p className="text-lg font-medium">No recent leads</p>
              <p className="text-sm mt-1">Set a status on a lead to see it here</p>
            </div>
          ) : (
            <>
              {recentLeads.leads.map((lead, idx) => (
                <LeadCard key={lead.id || idx} lead={lead} onSelect={handleSelectLead} />
              ))}
              {pagination && pagination.totalPages > 1 && (
                <div className="flex items-center justify-between py-3">
                  <button
                    onClick={() => loadRecentLeads(recentPage - 1)}
                    disabled={recentPage <= 1}
                    className="flex items-center gap-1 min-h-[44px] px-4 rounded-xl bg-white border text-sm font-medium disabled:opacity-30"
                  >
                    <ChevronLeft size={16} /> Prev
                  </button>
                  <span className="text-sm text-gray-500">
                    {recentPage} / {pagination.totalPages}
                  </span>
                  <button
                    onClick={() => loadRecentLeads(recentPage + 1)}
                    disabled={recentPage >= pagination.totalPages}
                    className="flex items-center gap-1 min-h-[44px] px-4 rounded-xl bg-white border text-sm font-medium disabled:opacity-30"
                  >
                    Next <ChevronRight size={16} />
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {activeTab === 'callbacks' && (
        <div className="flex-1 overflow-y-auto p-3 space-y-2">
          {callbacks.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <p className="text-lg font-medium">No callbacks scheduled</p>
              <p className="text-sm mt-1">Schedule callbacks from lead cards</p>
            </div>
          ) : (
            callbacks.map((cb, idx) => (
              <CallbackCard key={cb.id || idx} callback={cb} onSelect={handleSelectLead} />
            ))
          )}
        </div>
      )}

      {/* Modals */}
      {callLogLead && (
        <CallLogModal lead={callLogLead} onSave={handleCallLogSave} onClose={() => setCallLogLead(null)} saving={saving} />
      )}
      {callbackLead && (
        <CallbackModal lead={callbackLead} onSave={handleCallbackSave} onClose={() => setCallbackLead(null)} saving={saving} />
      )}
      {conversionLead && (
        <ConversionFlow lead={conversionLead} onConvert={handleConvert} onClose={handleConversionClose} saving={saving} />
      )}
    </div>
  );
};

/**
 * Callback card - shows callback-specific info (due time, business name)
 */
const CallbackCard = ({ callback, onSelect }) => {
  const isOverdue = new Date(callback.callback_datetime) < new Date();
  const lead = {
    ...callback,
    id: callback.lead_id,
    canvassing_status: callback.canvassing_status || 'callback',
  };

  return (
    <button
      onClick={() => onSelect(lead)}
      className="w-full text-left bg-white rounded-xl shadow-sm border border-gray-200 p-3 active:bg-gray-50 transition-all"
    >
      <div className="flex items-center gap-2 mb-1">
        <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${isOverdue ? 'bg-red-500 text-white' : 'bg-amber-100 text-amber-700'}`}>
          {isOverdue ? 'OVERDUE' : 'Due'}
        </span>
        <span className="text-xs text-gray-500">
          {formatDate(callback.callback_datetime)}
        </span>
      </div>
      <h3 className="font-bold text-gray-900 text-sm">{callback.business_name}</h3>
      {callback.phone_number && (
        <div className="text-xs text-green-600 mt-0.5">{callback.phone_number}</div>
      )}
      {callback.callback_notes && (
        <div className="text-xs text-gray-500 mt-1 line-clamp-1">{callback.callback_notes}</div>
      )}
    </button>
  );
};

export default CanvassingMobile;
